import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, PaperclipIcon, FileIcon, ImageIcon, FileTextIcon, MusicIcon, VideoIcon } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
interface Student {
  id: number;
  name: string;
  avatar?: string;
}
interface Message {
  id: number;
  senderId: string | number;
  text: string;
  timestamp: string;
  isTeacher: boolean;
  attachmentUrl?: string;
  attachmentType?: 'image' | 'pdf' | 'audio' | 'video' | 'doc';
  attachmentName?: string;
}
interface MessagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student;
  darkMode: boolean;
}
export const MessagingModal: React.FC<MessagingModalProps> = ({
  isOpen,
  onClose,
  student,
  darkMode
}) => {
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Initialize with sample messages
  useEffect(() => {
    if (isOpen && student) {
      // Create mock conversation history based on student
      const mockMessages = [{
        id: 1,
        senderId: student.id,
        text: `Hello! I'm looking forward to our next lesson.`,
        timestamp: '10:30 AM',
        isTeacher: false
      }, {
        id: 2,
        senderId: 'teacher',
        text: `Hi ${student.name}! I'm preparing some new material for you. How is your practice going?`,
        timestamp: '10:35 AM',
        isTeacher: true
      }, {
        id: 3,
        senderId: student.id,
        text: "I've been practicing daily. The new piece is challenging but I'm making progress!",
        timestamp: '10:40 AM',
        isTeacher: false
      }];
      setMessages(mockMessages);
    }
  }, [isOpen, student]);
  // Handle sending a new message
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event propagation
    if (!messageText.trim()) return;
    const newMessage = {
      id: messages.length + 1,
      senderId: 'teacher',
      text: messageText,
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      isTeacher: true
    };
    setMessages([...messages, newMessage]);
    setMessageText('');
    // Ensure immediate scroll to bottom after sending
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      }
    }, 50);
    return false; // Extra precaution against form submission
  };
  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    try {
      setIsUploading(true);
      // Create a unique file path
      const conversationId = `teacher-student-${student.id}`;
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `messageAttachments/${conversationId}/${fileName}`;
      // Upload to Firebase Storage
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      // Determine file type
      let attachmentType: 'image' | 'pdf' | 'audio' | 'video' | 'doc' = 'doc';
      if (file.type.startsWith('image/')) attachmentType = 'image';else if (file.type.startsWith('audio/')) attachmentType = 'audio';else if (file.type.startsWith('video/')) attachmentType = 'video';else if (file.type === 'application/pdf') attachmentType = 'pdf';
      // Create message with attachment
      const newMessage: Message = {
        id: messages.length + 1,
        senderId: 'teacher',
        text: '',
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        }),
        isTeacher: true,
        attachmentUrl: downloadUrl,
        attachmentType,
        attachmentName: file.name
      };
      setMessages([...messages, newMessage]);
      // Ensure immediate scroll to bottom after sending
      setTimeout(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  // Trigger file input click
  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Render attachment based on type
  const renderAttachment = (message: Message) => {
    if (!message.attachmentUrl) return null;
    switch (message.attachmentType) {
      case 'image':
        return <div className="mt-2 max-w-full" data-id="element-1804">
            <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" data-id="element-1805">
              <img src={message.attachmentUrl} alt="Attachment" className="max-w-full rounded-lg max-h-60 object-contain" data-id="element-1806" />
            </a>
          </div>;
      case 'pdf':
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${darkMode ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-1807">
            <FileTextIcon className="h-6 w-6 mr-2 text-red-500" data-id="element-1808" />
            <span className="text-sm truncate" data-id="element-1809">
              {message.attachmentName || 'PDF Document'}
            </span>
          </a>;
      case 'audio':
        return <div className="mt-2" data-id="element-1810">
            <div className={`flex items-center p-2 rounded-lg ${darkMode ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-1811">
              <MusicIcon className="h-6 w-6 mr-2 text-purple-500" data-id="element-1812" />
              <span className="text-sm truncate" data-id="element-1813">
                {message.attachmentName || 'Audio File'}
              </span>
            </div>
            <audio controls className="mt-2 max-w-full" data-id="element-1814">
              <source src={message.attachmentUrl} data-id="element-1815" />
              Your browser does not support audio playback.
            </audio>
          </div>;
      case 'video':
        return <div className="mt-2" data-id="element-1816">
            <video controls className="max-w-full rounded-lg max-h-60" data-id="element-1817">
              <source src={message.attachmentUrl} data-id="element-1818" />
              Your browser does not support video playback.
            </video>
          </div>;
      default:
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${darkMode ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-1819">
            <FileIcon className="h-6 w-6 mr-2 text-blue-500" data-id="element-1820" />
            <span className="text-sm truncate" data-id="element-1821">
              {message.attachmentName || 'Document'}
            </span>
          </a>;
    }
  };
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messageContainerRef.current) {
      // Use direct container scrolling
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1822">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20" data-id="element-1823">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-1824">
          <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-1825"></div>
        </div>
        <div className={`relative inline-block w-full max-w-2xl rounded-lg text-left overflow-hidden shadow-xl transform transition-all ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-1826">
          {/* Header */}
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex items-center`} data-id="element-1827">
            <div className="h-10 w-10 rounded-full overflow-hidden mr-3 bg-gradient-to-br from-deep-pink to-burgundy" data-id="element-1828">
              {student.avatar ? <img src={student.avatar} alt={student.name} className="h-full w-full object-cover" data-id="element-1829" /> : <div className="h-full w-full flex items-center justify-center text-white font-bold" data-id="element-1830">
                  {student.name.charAt(0)}
                </div>}
            </div>
            <div className="flex-1" data-id="element-1831">
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-1832">
                {student.name}
              </h3>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1833">
                Student
              </p>
            </div>
            <button onClick={onClose} className={`rounded-full p-2 ${darkMode ? 'hover:bg-navy-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'} transition-colors`} data-id="element-1834">
              <XIcon className="h-5 w-5" data-id="element-1835" />
            </button>
          </div>
          {/* Messages */}
          <div ref={messageContainerRef} className={`h-[60vh] max-h-[600px] overflow-y-auto p-4 ${darkMode ? 'bg-navy-900/30' : 'bg-gray-50'}`} data-id="element-1836">
            <div className="space-y-4" data-id="element-1837">
              {messages.map(message => <div key={message.id} className={`flex ${message.isTeacher ? 'justify-end' : 'justify-start'}`} data-id="element-1838">
                  <div className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 ${message.isTeacher ? darkMode ? 'bg-deep-pink text-white' : 'bg-deep-pink text-white' : darkMode ? 'bg-navy-700 text-white' : 'bg-white text-gray-800 border border-gray-200'}`} data-id="element-1839">
                    {message.text && <p className="text-sm" data-id="element-1840">{message.text}</p>}
                    {message.attachmentUrl && renderAttachment(message)}
                    <p className={`text-xs mt-1 ${message.isTeacher ? 'text-pink-100' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1841">
                      {message.timestamp}
                    </p>
                  </div>
                </div>)}
              <div ref={messageEndRef} data-id="element-1842" />
            </div>
          </div>
          {/* Message Input */}
          <div className={`px-4 py-3 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-1843">
            <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-1844">
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.mp3,.mp4,.docx,.txt" data-id="element-1845" />
              <button type="button" onClick={handleAttachmentClick} disabled={isUploading} className={`p-2 rounded-full ${isUploading ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-1846">
                <PaperclipIcon className="h-5 w-5" data-id="element-1847" />
              </button>
              <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} placeholder={isUploading ? 'Uploading...' : 'Type a message...'} disabled={isUploading} className={`flex-1 mx-3 py-2 px-4 rounded-full focus:outline-none focus:ring-1 focus:ring-deep-pink ${isUploading ? 'opacity-70' : ''} ${darkMode ? 'bg-navy-700 text-white placeholder-gray-400 border-navy-600' : 'bg-gray-100 text-gray-900 placeholder-gray-500 border-gray-200'} border`} data-id="element-1848" />
              <button type="submit" disabled={!messageText.trim() || isUploading} className={`p-2 rounded-full ${messageText.trim() && !isUploading ? 'bg-deep-pink text-white' : darkMode ? 'bg-navy-700 text-gray-400' : 'bg-gray-200 text-gray-500'} transition-colors`} data-id="element-1849">
                <SendIcon className="h-5 w-5" data-id="element-1850" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>;
};